/**
 * Address Book — standalone manager for the personal contact store
 * (users/{uid}/contacts, task #510).
 *
 * Extracted from bulk-sharing-wizard.js so account-level pages (My Projects)
 * can offer it without loading the 13k-line wizard. The wizard now delegates
 * here: its openAddressBookManager() lazy-loads this file on first use, so
 * dashboard/builder pages need no extra <script> tag. My Projects loads it
 * with a normal deferred tag and calls AddressBook.open() from its button.
 *
 * Self-contained i18n: a small built-in dictionary (7 languages) resolved
 * from the URL locale prefix; callers with richer context (the wizard) can
 * pass their own translation object via open({ t }) and it wins per-key.
 */
(function () {
    'use strict';

    const I18N = {
        en: {
            addressBook: 'Address Book',
            addContact: 'Add Contact',
            deleteContactConfirm: 'Delete this contact from your address book?',
            contactNeedsInfo: 'A contact needs a name and an email or phone.',
            noSavedContacts: 'No saved contacts yet. Contacts you message in a campaign are saved to your address book automatically.',
            searchContactsPlaceholder: 'Search contacts...',
            name: 'Name', email: 'Email', phone: 'Phone', tags: 'Tags',
            save: 'Save', cancel: 'Cancel', edit: 'Edit', delete: 'Delete',
            failedToLoadContacts: 'Failed to load contacts: '
        },
        es: {
            addressBook: 'Libreta de Direcciones',
            addContact: 'Agregar Contacto',
            deleteContactConfirm: '¿Eliminar este contacto de tu libreta de direcciones?',
            contactNeedsInfo: 'Un contacto necesita un nombre y un correo o teléfono.',
            noSavedContacts: 'Aún no hay contactos guardados. Los contactos a los que envías mensajes en una campaña se guardan automáticamente en tu libreta de direcciones.',
            searchContactsPlaceholder: 'Buscar contactos...',
            name: 'Nombre', email: 'Correo', phone: 'Teléfono', tags: 'Etiquetas',
            save: 'Guardar', cancel: 'Cancelar', edit: 'Editar', delete: 'Eliminar',
            failedToLoadContacts: 'No se pudieron cargar los contactos: '
        },
        fr: {
            addressBook: "Carnet d'adresses",
            addContact: 'Ajouter un contact',
            deleteContactConfirm: 'Supprimer ce contact de votre carnet d’adresses ?',
            contactNeedsInfo: 'Un contact doit avoir un nom et un e-mail ou un téléphone.',
            noSavedContacts: 'Aucun contact enregistré pour l’instant. Les contacts auxquels vous envoyez des messages dans une campagne sont automatiquement enregistrés dans votre carnet d’adresses.',
            searchContactsPlaceholder: 'Rechercher des contacts...',
            name: 'Nom', email: 'E-mail', phone: 'Téléphone', tags: 'Étiquettes',
            save: 'Enregistrer', cancel: 'Annuler', edit: 'Modifier', delete: 'Supprimer',
            failedToLoadContacts: 'Impossible de charger les contacts : '
        },
        de: {
            addressBook: 'Adressbuch',
            addContact: 'Kontakt hinzufügen',
            deleteContactConfirm: 'Diesen Kontakt aus Ihrem Adressbuch löschen?',
            contactNeedsInfo: 'Ein Kontakt braucht einen Namen und eine E-Mail oder Telefonnummer.',
            noSavedContacts: 'Noch keine gespeicherten Kontakte. Kontakte, die Sie in einer Kampagne anschreiben, werden automatisch in Ihrem Adressbuch gespeichert.',
            searchContactsPlaceholder: 'Kontakte suchen...',
            name: 'Name', email: 'E-Mail', phone: 'Telefon', tags: 'Tags',
            save: 'Speichern', cancel: 'Abbrechen', edit: 'Bearbeiten', delete: 'Löschen',
            failedToLoadContacts: 'Kontakte konnten nicht geladen werden: '
        },
        pt: {
            addressBook: 'Lista de Endereços',
            addContact: 'Adicionar Contato',
            deleteContactConfirm: 'Excluir este contato da sua lista de endereços?',
            contactNeedsInfo: 'Um contato precisa de um nome e um e-mail ou telefone.',
            noSavedContacts: 'Ainda não há contatos salvos. Os contatos para quem você envia mensagens em uma campanha são salvos automaticamente na sua lista de endereços.',
            searchContactsPlaceholder: 'Pesquisar contatos...',
            name: 'Nome', email: 'E-mail', phone: 'Telefone', tags: 'Etiquetas',
            save: 'Salvar', cancel: 'Cancelar', edit: 'Editar', delete: 'Excluir',
            failedToLoadContacts: 'Não foi possível carregar os contatos: '
        },
        he: {
            addressBook: 'פנקס כתובות',
            addContact: 'הוספת איש קשר',
            deleteContactConfirm: 'למחוק את איש הקשר הזה מפנקס הכתובות שלכם?',
            contactNeedsInfo: 'איש קשר צריך שם וכתובת אימייל או טלפון.',
            noSavedContacts: 'אין עדיין אנשי קשר שמורים. אנשי קשר שאתם שולחים אליהם הודעות בקמפיין נשמרים אוטומטית בפנקס הכתובות שלכם.',
            searchContactsPlaceholder: 'חיפוש אנשי קשר...',
            name: 'שם', email: 'אימייל', phone: 'טלפון', tags: 'תגיות',
            save: 'שמירה', cancel: 'ביטול', edit: 'עריכה', delete: 'מחיקה',
            failedToLoadContacts: 'טעינת אנשי הקשר נכשלה: '
        },
        ar: {
            addressBook: 'دفتر العناوين',
            addContact: 'إضافة جهة اتصال',
            deleteContactConfirm: 'هل تريد حذف جهة الاتصال هذه من دفتر العناوين الخاص بك؟',
            contactNeedsInfo: 'تحتاج جهة الاتصال إلى اسم وبريد إلكتروني أو هاتف.',
            noSavedContacts: 'لا توجد جهات اتصال محفوظة بعد. يتم حفظ جهات الاتصال التي تراسلها في أي حملة تلقائيًا في دفتر العناوين الخاص بك.',
            searchContactsPlaceholder: 'البحث في جهات الاتصال...',
            name: 'الاسم', email: 'البريد الإلكتروني', phone: 'الهاتف', tags: 'الوسوم',
            save: 'حفظ', cancel: 'إلغاء', edit: 'تعديل', delete: 'حذف',
            failedToLoadContacts: 'تعذر تحميل جهات الاتصال: '
        }
    };

    function pageLang() {
        const m = window.location.pathname.match(/^\/(es|fr|de|pt|he|ar)(\/|$)/);
        return (m && m[1]) || window.DASHBOARD_LANGUAGE || 'en';
    }

    const AddressBook = {
        _contacts: [],
        _t: I18N.en,
        _onChanged: null,

        /**
         * Open the manager. opts.t — caller translations, win per-key over the
         * built-ins. opts.onChanged — called after the first successful write,
         * so hosting UIs can reveal pickers gated on a non-empty book.
         */
        async open(opts = {}) {
            const user = window.firebaseAuth?.currentUser;
            if (!user) return;

            const base = I18N[pageLang()] || I18N.en;
            // Caller keys win, but only where the caller actually has the key
            // (the wizard's t is a large object with many unrelated keys).
            const callerT = opts.t || {};
            this._t = new Proxy(base, {
                get: (target, key) => callerT[key] || target[key] || I18N.en[key]
            });
            this._onChanged = typeof opts.onChanged === 'function' ? opts.onChanged : null;

            // If the wizard's picker is open beneath us, close it.
            const picker = document.getElementById('orgContactsPickerModal');
            if (picker && window.bootstrap) bootstrap.Modal.getInstance(picker)?.hide();

            let contacts = [];
            try {
                const token = await user.getIdToken();
                const res = await fetch('/api/users/me/contacts', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) contacts = (await res.json()).contacts || [];
            } catch (err) {
                console.warn('Could not load address book:', err);
            }

            // Newest first; docs missing createdAt sort last (server list is unordered).
            contacts.sort((a, b) => (b.createdAt?._seconds || 0) - (a.createdAt?._seconds || 0));
            this._contacts = contacts;
            this._render();
        },

        _esc(s) {
            return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        },

        _rowHtml(c) {
            const esc = this._esc;
            return `
            <tr class="ab-row" data-contact-id="${esc(c.id)}" data-name="${esc(c.name)}" data-email="${esc(c.email || '')}" data-phone="${esc(c.phone || '')}" data-tags="${esc((c.tags || []).join(','))}">
              <td class="small fw-semibold ab-cell-name">${esc(c.name)}</td>
              <td class="small text-muted ab-cell-email">${c.email ? esc(c.email) : '—'}</td>
              <td class="small text-muted ab-cell-phone">${c.phone ? esc(c.phone) : '—'}</td>
              <td class="small ab-cell-tags">${(c.tags || []).map(t => `<span class="badge bg-secondary me-1">${esc(t)}</span>`).join('')}</td>
              <td class="text-nowrap text-end">
                <button type="button" class="btn btn-sm btn-outline-secondary ab-edit-btn" title="${esc(this._t.edit)}"><i class="bi bi-pencil"></i></button>
                <button type="button" class="btn btn-sm btn-outline-danger ab-delete-btn" title="${esc(this._t.delete)}"><i class="bi bi-trash"></i></button>
              </td>
            </tr>`;
        },

        _editRowHtml(c) {
            const esc = this._esc;
            return `
            <tr class="ab-row ab-row-editing" data-contact-id="${esc(c.id || '')}">
              <td><input type="text" class="form-control form-control-sm ab-in-name" value="${esc(c.name)}" placeholder="${esc(this._t.name)}"></td>
              <td><input type="email" class="form-control form-control-sm ab-in-email" value="${esc(c.email || '')}" placeholder="${esc(this._t.email)}" dir="ltr"></td>
              <td><input type="tel" class="form-control form-control-sm ab-in-phone" value="${esc(c.phone || '')}" placeholder="${esc(this._t.phone)}" dir="ltr"></td>
              <td><input type="text" class="form-control form-control-sm ab-in-tags" value="${esc((c.tags || []).join(', '))}" placeholder="${esc(this._t.tags)}"></td>
              <td class="text-nowrap text-end">
                <button type="button" class="btn btn-sm btn-success ab-save-btn" title="${esc(this._t.save)}"><i class="bi bi-check-lg"></i></button>
                <button type="button" class="btn btn-sm btn-outline-secondary ab-cancel-btn" title="${esc(this._t.cancel)}"><i class="bi bi-x-lg"></i></button>
              </td>
            </tr>`;
        },

        _render() {
            const modalId = 'addressBookModal';
            document.getElementById(modalId)?.remove();
            const esc = this._esc;
            const contacts = this._contacts || [];

            const html = `
            <div class="modal fade" id="${modalId}" tabindex="-1">
              <div class="modal-dialog modal-lg">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title"><i class="bi bi-journal-text me-2"></i>${esc(this._t.addressBook)}
                      <span class="badge bg-secondary ms-2" id="abCountBadge">${contacts.length}</span>
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                  </div>
                  <div class="modal-body">
                    <div class="d-flex gap-2 mb-3">
                      <input type="text" class="form-control form-control-sm" id="abSearch" placeholder="${esc(this._t.searchContactsPlaceholder)}" style="flex:1;">
                      <button class="btn btn-sm btn-primary text-nowrap" type="button" id="abAddBtn"><i class="bi bi-plus-circle me-1"></i>${esc(this._t.addContact)}</button>
                    </div>
                    <div class="text-center text-muted py-4" id="abEmptyState" style="display:${contacts.length ? 'none' : 'block'};">
                      ${esc(this._t.noSavedContacts)}
                    </div>
                    <div class="table-responsive" id="abTableWrap" style="max-height: 380px; overflow-y: auto; display:${contacts.length ? 'block' : 'none'};">
                      <table class="table table-sm table-hover align-middle">
                        <thead class="table-light sticky-top">
                          <tr>
                            <th>${esc(this._t.name)}</th>
                            <th>${esc(this._t.email)}</th>
                            <th>${esc(this._t.phone)}</th>
                            <th>${esc(this._t.tags)}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody id="abTableBody">
                          ${contacts.map(c => this._rowHtml(c)).join('')}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>`;

            document.body.insertAdjacentHTML('beforeend', html);
            const modalEl = document.getElementById(modalId);
            const modal = new bootstrap.Modal(modalEl);
            modal.show();
            modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());

            document.getElementById('abSearch')?.addEventListener('input', (e) => {
                const q = e.target.value.toLowerCase();
                modalEl.querySelectorAll('.ab-row:not(.ab-row-editing)').forEach(row => {
                    const match = !q ||
                        (row.dataset.name || '').toLowerCase().includes(q) ||
                        (row.dataset.email || '').toLowerCase().includes(q) ||
                        (row.dataset.phone || '').includes(q) ||
                        (row.dataset.tags || '').toLowerCase().includes(q);
                    row.style.display = match ? '' : 'none';
                });
            });

            document.getElementById('abAddBtn')?.addEventListener('click', () => {
                const body = document.getElementById('abTableBody');
                if (!body || body.querySelector('.ab-row-editing[data-contact-id=""]')) return;
                document.getElementById('abEmptyState').style.display = 'none';
                document.getElementById('abTableWrap').style.display = 'block';
                body.insertAdjacentHTML('afterbegin', this._editRowHtml({ name: '', email: '', phone: '', tags: [] }));
                body.querySelector('.ab-in-name')?.focus();
            });

            // Enter saves, Escape cancels — the same keys the inline editors
            // elsewhere in the dashboard honour, and the ones a host reaches for
            // after typing in the last box (Tags). See the matching note in
            // bulk-sharing-wizard.js (task #571 round 2).
            modalEl.addEventListener('keydown', (e) => {
                const row = e.target.closest && e.target.closest('.ab-row-editing');
                if (!row || !e.target.matches('input')) return;
                if (e.key === 'Enter') { e.preventDefault(); row.querySelector('.ab-save-btn')?.click(); }
                else if (e.key === 'Escape') { e.preventDefault(); row.querySelector('.ab-cancel-btn')?.click(); }
            });

            // One delegated listener covers rows added later
            modalEl.addEventListener('click', async (e) => {
                const row = e.target.closest('.ab-row');
                if (!row) return;
                const id = row.dataset.contactId;

                if (e.target.closest('.ab-edit-btn')) {
                    const c = (this._contacts || []).find(x => x.id === id);
                    if (c) row.outerHTML = this._editRowHtml(c);
                    modalEl.querySelector('.ab-row-editing .ab-in-name')?.focus();
                    return;
                }

                if (e.target.closest('.ab-cancel-btn')) {
                    const c = (this._contacts || []).find(x => x.id === id);
                    if (c) row.outerHTML = this._rowHtml(c);
                    else row.remove(); // cancelled a new-contact row
                    this._syncEmptyState();
                    return;
                }

                if (e.target.closest('.ab-delete-btn')) {
                    if (!window.confirm(this._t.deleteContactConfirm)) return;
                    try {
                        await this._request(`/api/users/me/contacts/${encodeURIComponent(id)}`, 'DELETE');
                        this._contacts = (this._contacts || []).filter(x => x.id !== id);
                        row.remove();
                        this._syncEmptyState();
                    } catch (err) {
                        alert(this._t.failedToLoadContacts + (err.message || ''));
                    }
                    return;
                }

                if (e.target.closest('.ab-save-btn')) {
                    const name = row.querySelector('.ab-in-name')?.value.trim() || '';
                    const email = row.querySelector('.ab-in-email')?.value.trim() || '';
                    const phone = row.querySelector('.ab-in-phone')?.value.trim() || '';
                    const tags = (row.querySelector('.ab-in-tags')?.value || '').split(',').map(t => t.trim()).filter(Boolean);
                    if (!name || (!email && !phone)) {
                        alert(this._t.contactNeedsInfo);
                        return;
                    }
                    const saveBtn = row.querySelector('.ab-save-btn');
                    saveBtn.disabled = true;
                    try {
                        let saved;
                        if (id) {
                            saved = (await this._request(`/api/users/me/contacts/${encodeURIComponent(id)}`, 'PUT', { name, email, phone, tags })).contact;
                            this._contacts = (this._contacts || []).map(x => x.id === id ? { ...x, ...saved } : x);
                        } else {
                            saved = (await this._request('/api/users/me/contacts', 'POST', { name, email, phone, tags })).contact;
                            this._contacts = [saved, ...(this._contacts || [])];
                            this._onChanged?.();
                        }
                        row.outerHTML = this._rowHtml(saved);
                        this._syncEmptyState();
                    } catch (err) {
                        saveBtn.disabled = false;
                        alert(this._t.failedToLoadContacts + (err.message || ''));
                    }
                }
            });
        },

        _syncEmptyState() {
            const n = document.querySelectorAll('#abTableBody .ab-row').length;
            const badge = document.getElementById('abCountBadge');
            const empty = document.getElementById('abEmptyState');
            const wrap = document.getElementById('abTableWrap');
            if (badge) badge.textContent = (this._contacts || []).length;
            if (empty) empty.style.display = n ? 'none' : 'block';
            if (wrap) wrap.style.display = n ? 'block' : 'none';
        },

        async _request(url, method, body) {
            const user = window.firebaseAuth?.currentUser;
            if (!user) throw new Error('Not signed in');
            const token = await user.getIdToken();
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: body ? JSON.stringify(body) : undefined
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
            return data;
        }
    };

    window.AddressBook = AddressBook;
})();
